import { MODES } from "@/lib/interviewModes";
import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api, ApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/format";
import { Spinner } from "@/components/primitives";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  accentLight: "var(--accent-50)",
  slate: "#7C7590",
  good: "#1A7A4A",
  warn: "#B5811F",
  white: "#FFFFFF",
  border: "#E8E4DC",
  navy: "#1A3552",
  error: "#B91C1C",
  errorBg: "rgba(185,28,28,0.06)",
  errorBorder: "rgba(185,28,28,0.25)",
} as const;

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: T.accent,
};

const fieldLabel: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: T.ink,
  letterSpacing: "0.03em",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  fontSize: 13.5,
  color: T.ink,
  background: T.white,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.12s",
  fontFamily: "inherit",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: "vertical",
  minHeight: 90,
};

const sectionHeading: React.CSSProperties = {
  margin: "0 0 14px",
  fontSize: 14,
  fontWeight: 700,
  color: T.ink,
};

// ── Data ──────────────────────────────────────────────────────────────────────
const PERSONAS = [
  {
    id: "internal-hiring-manager",
    label: "Internal Hiring Manager",
    style: "Direct",
    desc: "Forensic and unhurried. Knows the team, culture, and gap. Probes for specifics and challenges inconsistencies directly.",
  },
  {
    id: "cmo-panel-chair",
    label: "CMO Panel Chair",
    style: "Probing",
    desc: "Impatient, scale-first. Wants to see commercial instinct and board-level presence. Will interrupt to test composure.",
  },
  {
    id: "exec-search-partner",
    label: "Exec-Search Partner",
    style: "Empathetic",
    desc: "Narrative-first. Builds rapport before probing. Focused on candidate journey, motivations, and authentic story arc.",
  },
  {
    id: "founder",
    label: "Founder / CEO",
    style: "Structured",
    desc: "Hypothesis-first. Treats every answer as data. Will challenge assumptions and reward candidates who think like owners.",
  },
] as const;



type PersonaId = (typeof PERSONAS)[number]["id"];
type ModeId = (typeof MODES)[number]["id"];
type ExpiryDays = 3 | 7 | 14 | 30;

// ── Sub-components ────────────────────────────────────────────────────────────
function PersonaCard({
  persona,
  selected,
  onSelect,
}: {
  persona: (typeof PERSONAS)[number];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "14px 16px",
        border: `2px solid ${selected ? T.accent : T.border}`,
        borderRadius: 10,
        background: selected ? "rgba(26,53,82,0.04)" : T.white,
        cursor: "pointer",
        transition: "border-color 0.12s, background 0.12s",
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: T.ink,
          }}
        >
          {persona.label}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: selected ? T.accent : T.slate,
            background: selected ? "rgba(26,53,82,0.08)" : "rgba(107,122,140,0.1)",
            padding: "2px 7px",
            borderRadius: 4,
          }}
        >
          {persona.style}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 12,
          color: T.slate,
          lineHeight: 1.5,
        }}
      >
        {persona.desc}
      </p>
    </button>
  );
}

function ModeCard({
  mode,
  selected,
  onSelect,
}: {
  mode: (typeof MODES)[number];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "12px 16px",
        border: `2px solid ${selected ? T.accent : T.border}`,
        borderRadius: 10,
        background: selected ? T.accentLight : T.white,
        cursor: "pointer",
        transition: "border-color 0.12s, background 0.12s",
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: T.ink,
          }}
        >
          {mode.label}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: selected ? T.accent : T.slate,
            background: selected ? "rgba(122,107,224,0.1)" : "rgba(107,122,140,0.1)",
            padding: "2px 7px",
            borderRadius: 4,
            whiteSpace: "nowrap",
          }}
        >
          {mode.tag}
        </span>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 12,
          color: T.slate,
          lineHeight: 1.5,
        }}
      >
        {mode.desc}
      </p>
    </button>
  );
}

// ── Success state ─────────────────────────────────────────────────────────────
function SuccessPanel({
  inviteLink,
  emailSent,
  uploadWarning,
  onSendAnother,
}: {
  inviteLink: string;
  emailSent: boolean;
  uploadWarning: string | null;
  onSendAnother: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const handleCopy = () => {
    setCopyFailed(false);
    copyToClipboard(inviteLink).then((ok) => {
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        setCopyFailed(true);
      }
    });
  };

  return (
    <div
      style={{
        background: T.white,
        borderRadius: 12,
        padding: 32,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        maxWidth: 560,
        margin: "0 auto",
      }}
    >
      {/* Checkmark — left-anchored to match the page's own header and every
          other recruiter page's convention (was centred, sitting oddly next
          to the left-aligned "Send invite" title above it). */}
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "rgba(26,122,74,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 20,
        }}
      >
        <span style={{ fontSize: 24, color: T.good }}>✓</span>
      </div>

      <h2
        style={{
          margin: "0 0 8px",
          fontSize: 20,
          fontWeight: 700,
          color: T.ink,
        }}
      >
        Invite link created
      </h2>
      <p
        style={{
          margin: "0 0 16px",
          fontSize: 13.5,
          color: T.slate,
          lineHeight: 1.6,
        }}
      >
        {emailSent
          ? "We've emailed this link to the candidate. It will expire after the selected period."
          : "Share this link with your candidate directly — we could not send it by email."}
      </p>

      {(!emailSent || uploadWarning) && (
        <div
          style={{
            background: "rgba(181,129,31,0.08)",
            border: "1px solid rgba(181,129,31,0.25)",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 20,
            textAlign: "left",
            fontSize: 12.5,
            color: T.warn,
            lineHeight: 1.5,
          }}
        >
          {!emailSent && <p style={{ margin: uploadWarning ? "0 0 4px" : 0 }}>Email delivery is not configured — copy the link below and send it yourself.</p>}
          {uploadWarning && <p style={{ margin: 0 }}>{uploadWarning}</p>}
        </div>
      )}

      {/* Link box */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          background: "#F3F4F6",
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          padding: "10px 14px",
          marginBottom: 20,
          textAlign: "left",
        }}
      >
        <span
          style={{
            flex: 1,
            fontSize: 12.5,
            color: T.ink,
            fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {inviteLink}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          style={{
            flexShrink: 0,
            padding: "5px 14px",
            background: copied ? T.good : copyFailed ? T.error : T.accent,
            color: T.white,
            border: "none",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            transition: "background 0.15s",
          }}
        >
          {copied ? "Copied!" : copyFailed ? "Couldn't copy — select manually" : "Copy"}
        </button>
      </div>

      <button
        type="button"
        onClick={onSendAnother}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: `1px solid ${T.border}`,
          borderRadius: 999,
          padding: "8px 20px",
          fontSize: 13.5,
          fontWeight: 600,
          color: T.ink,
          cursor: "pointer",
          transition: "border-color 0.12s",
        }}
      >
        ← Send another invite
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// ── File dropzone (CV / JD upload) ──────────────────────────────────────────
function FileDropzone({
  id,
  hint,
  file,
  onChange,
  accept,
}: {
  id: string;
  hint: string;
  file: File | null;
  onChange: (f: File | null) => void;
  accept: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {file ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            padding: "10px 14px",
            background: T.white,
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: 12.5,
              color: T.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.name}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            style={{
              flexShrink: 0,
              background: "none",
              border: `1px solid ${T.border}`,
              borderRadius: 6,
              padding: "4px 10px",
              color: T.slate,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Remove
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onChange(f);
          }}
          style={{
            border: `2px dashed ${dragOver ? T.accent : T.border}`,
            borderRadius: 8,
            padding: "20px 16px",
            textAlign: "center",
            background: dragOver ? T.accentLight : "#FAFAFA",
            cursor: "pointer",
          }}
        >
          <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 500, color: T.ink }}>
            Click to upload or drag &amp; drop
          </p>
          <p style={{ margin: 0, fontSize: 11.5, color: T.slate }}>{hint}</p>
        </div>
      )}
    </div>
  );
}

export function RecruiterSendInvite() {
  // "New invite" links from the candidates list / candidate detail page pass
  // ?email= to prefill a repeat invite -- only ever email, no name/role/company.
  const [searchParams] = useSearchParams();

  // Form state
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState(() => searchParams.get("email") ?? "");
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [jdUrl, setJdUrl] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [expiryDays, setExpiryDays] = useState<ExpiryDays>(7);
  const [persona, setPersona] = useState<PersonaId>("internal-hiring-manager");
  const [mode, setMode] = useState<ModeId>("full");
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);

  // Async state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      const form = new FormData();
      form.set("candidate_name", candidateName);
      form.set("candidate_email", candidateEmail);
      form.set("role", role);
      form.set("company", company);
      if (jdUrl) form.set("jd_url", jdUrl);
      if (customInstructions) form.set("custom_instructions", customInstructions);
      form.set("expiry_days", String(expiryDays));
      form.set("persona", persona);
      form.set("mode", mode);
      if (jdFile) form.set("jd_file", jdFile);
      if (cvFile) form.set("cv_file", cvFile);

      const result = await api.post<{
        ok: boolean;
        error?: string;
        invite_link?: string;
        email_sent?: boolean;
        cv_upload_warning?: string | null;
        jd_upload_warning?: string | null;
      }>("/recruiter/invite", form);
      if (!result.ok || !result.invite_link) {
        setError(result.error || "Failed to generate invite link. Please try again.");
        return;
      }
      setInviteLink(result.invite_link);
      setEmailSent(!!result.email_sent);
      // Both can fire independently (e.g. bad CV + bad JD in the same submit) —
      // concatenate rather than dropping one (was `a || b`, silently losing b).
      setUploadWarning(
        [result.cv_upload_warning, result.jd_upload_warning].filter(Boolean).join(" ") || null,
      );
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "Failed to generate invite link. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setInviteLink(null);
    setError(null);
    setCandidateName("");
    setCandidateEmail("");
    setRole("");
    setCompany("");
    setJdUrl("");
    setJdFile(null);
    setCvFile(null);
    setCustomInstructions("");
    setExpiryDays(7);
    setPersona("internal-hiring-manager");
    setMode("full");
    setEmailSent(false);
    setUploadWarning(null);
  };

  return (
    <RecruiterShell activeNav="invite">
      {/* ── Page header ───────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: "0 0 6px",
            fontSize: 26,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.2,
          }}
        >
          Send invite
        </h1>
        <p style={{ margin: 0, fontSize: 13.5, color: T.slate }}>
          Create a personalised interview link for a candidate.
        </p>
      </div>

      {/* ── Success state ─────────────────────────────────────────────── */}
      {inviteLink ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <SuccessPanel
            inviteLink={inviteLink}
            emailSent={emailSent}
            uploadWarning={uploadWarning}
            onSendAnother={handleSendAnother}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          {/* ── Error banner ──────────────────────────────────────────── */}
          {error && (
            <div
              role="alert"
              style={{
                background: T.errorBg,
                border: `1px solid ${T.errorBorder}`,
                borderRadius: 8,
                padding: "12px 16px",
                marginBottom: 24,
                fontSize: 13.5,
                color: T.error,
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <span style={{ flexShrink: 0, fontWeight: 700 }}>!</span>
              <span>{error}</span>
            </div>
          )}

          {/* ── Two-column grid ───────────────────────────────────────── */}
          <div
            className="invite-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 0.9fr)",
              gap: 24,
              alignItems: "start",
            }}
          >
            {/* ════════════════════════════════════════════════════════
                LEFT COLUMN — Candidate details
            ═══════════════════════════════════════════════════════════ */}
            <div
              style={{
                background: T.white,
                borderRadius: 12,
                padding: 28,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
            >
              <p style={{ ...monoLabel, marginBottom: 20 }}>CANDIDATE DETAILS</p>

              {/* Full name */}
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="candidate-name" style={fieldLabel}>
                  Full name <span style={{ color: T.error }}>*</span>
                </label>
                <input
                  id="candidate-name"
                  type="text"
                  required
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  placeholder="e.g. Sarah Okonkwo"
                  style={inputStyle}
                />
              </div>

              {/* Email */}
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="candidate-email" style={fieldLabel}>
                  Email address <span style={{ color: T.error }}>*</span>
                </label>
                <input
                  id="candidate-email"
                  type="email"
                  required
                  value={candidateEmail}
                  onChange={(e) => setCandidateEmail(e.target.value)}
                  placeholder="e.g. sarah@company.com"
                  style={inputStyle}
                />
              </div>

              {/* Role */}
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="role" style={fieldLabel}>
                  Role <span style={{ color: T.error }}>*</span>
                </label>
                <input
                  id="role"
                  type="text"
                  required
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g. Marketing Director"
                  style={inputStyle}
                />
              </div>

              {/* Company */}
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="company" style={fieldLabel}>
                  Company
                </label>
                <input
                  id="company"
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Acme Corp"
                  style={inputStyle}
                />
              </div>

              {/* Job description */}
              <div style={{ marginBottom: 18 }}>
                <label style={fieldLabel}>Job description</label>
                <p
                  style={{
                    margin: "0 0 10px",
                    fontSize: 12,
                    color: T.slate,
                    lineHeight: 1.5,
                  }}
                >
                  Paste a URL or upload the job description to personalise the session.
                </p>

                {/* URL input */}
                <input
                  id="jd-url"
                  type="url"
                  value={jdUrl}
                  onChange={(e) => setJdUrl(e.target.value)}
                  placeholder="https://example.com/jobs/marketing-director"
                  style={{ ...inputStyle, marginBottom: 10 }}
                />

                <FileDropzone
                  id="jd-file"
                  hint="PDF or DOCX — max 5 MB"
                  file={jdFile}
                  onChange={setJdFile}
                  accept=".pdf,.docx"
                />
              </div>

              {/* Candidate CV (optional recruiter pre-upload) */}
              <div style={{ marginBottom: 18 }}>
                <label style={fieldLabel}>
                  Candidate CV <span style={{ fontWeight: 400, color: T.slate }}>(optional)</span>
                </label>
                <p
                  style={{
                    margin: "0 0 10px",
                    fontSize: 12,
                    color: T.slate,
                    lineHeight: 1.5,
                  }}
                >
                  Pre-upload the candidate's CV so they skip that step and see "CV on file" when they start.
                </p>
                <FileDropzone
                  id="cv-file"
                  hint="PDF, DOC or DOCX — max 10 MB"
                  file={cvFile}
                  onChange={setCvFile}
                  accept=".pdf,.doc,.docx"
                />
              </div>

              {/* Custom instructions */}
              <div style={{ marginBottom: 22 }}>
                <label htmlFor="custom-instructions" style={fieldLabel}>
                  Custom instructions{" "}
                  <span style={{ fontWeight: 400, color: T.slate }}>(optional)</span>
                </label>
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 12,
                    color: T.slate,
                    lineHeight: 1.5,
                  }}
                >
                  Add any context the AI coach should know — specific competencies to probe, red
                  flags to watch for, or areas to avoid.
                </p>
                <textarea
                  id="custom-instructions"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="e.g. Focus on commercial leadership examples. Avoid questions about their gap year."
                  rows={3}
                  style={textareaStyle}
                />
              </div>

              {/* Link expiry */}
              <div style={{ marginBottom: 28 }}>
                <label style={fieldLabel}>Link expiry</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {([3, 7, 14, 30] as ExpiryDays[]).map((days) => (
                    <button
                      key={days}
                      type="button"
                      onClick={() => setExpiryDays(days)}
                      style={{
                        flex: 1,
                        padding: "8px 0",
                        border: `1.5px solid ${expiryDays === days ? T.accent : T.border}`,
                        borderRadius: 8,
                        background: expiryDays === days ? T.accent : T.white,
                        color: expiryDays === days ? T.white : T.slate,
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "all 0.12s",
                      }}
                    >
                      {days} days
                    </button>
                  ))}
                </div>
              </div>

              <div className="invite-submit-desktop">
              {/* Generate button */}
              <button
                type="submit"
                disabled={submitting || !candidateName || !candidateEmail || !role || !company}
                style={{
                  width: "100%",
                  padding: "12px 0",
                  background:
                    submitting || !candidateName || !candidateEmail || !role || !company
                      ? "#94A3B8"
                      : T.ink,
                  color: T.white,
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor:
                    submitting || !candidateName || !candidateEmail || !role
                      ? "default"
                      : "pointer",
                  transition: "background 0.15s",
                  letterSpacing: "0.01em",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {submitting && <Spinner size={14} />}
                {submitting ? "Generating…" : "Generate invite link"}
              </button>

              {/* Cancel */}
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <a
                  href="/coach/recruiter"
                  style={{
                    fontSize: 13,
                    color: T.slate,
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </a>
              </div>
              </div>
            </div>

            {/* ════════════════════════════════════════════════════════
                RIGHT COLUMN — Persona + Mode
            ═══════════════════════════════════════════════════════════ */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Interviewer persona section */}
              <div
                style={{
                  background: T.white,
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <p style={{ ...monoLabel, marginBottom: 4 }}>INTERVIEWER PERSONA</p>
                <h3 style={{ ...sectionHeading, marginTop: 6 }}>
                  Who will interview them?
                </h3>
                <p
                  style={{
                    margin: "0 0 16px",
                    fontSize: 12.5,
                    color: T.slate,
                    lineHeight: 1.5,
                  }}
                >
                  Choose the interviewer archetype to match the role and environment.
                </p>

                {PERSONAS.map((p) => (
                  <PersonaCard
                    key={p.id}
                    persona={p}
                    selected={persona === p.id}
                    onSelect={() => setPersona(p.id)}
                  />
                ))}
              </div>

              {/* Practice mode section */}
              <div
                style={{
                  background: T.white,
                  borderRadius: 12,
                  padding: 24,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <p style={{ ...monoLabel, marginBottom: 4 }}>PRACTICE MODE</p>
                <h3 style={{ ...sectionHeading, marginTop: 6 }}>
                  What should they practise?
                </h3>
                <p
                  style={{
                    margin: "0 0 16px",
                    fontSize: 12.5,
                    color: T.slate,
                    lineHeight: 1.5,
                  }}
                >
                  Select the session format for this candidate.
                </p>

                {MODES.map((m) => (
                  <ModeCard
                    key={m.id}
                    mode={m}
                    selected={mode === m.id}
                    onSelect={() => setMode(m.id)}
                  />
                ))}
              </div>
            </div>
            <div className="invite-submit-mobile">
              {/* Generate button */}
              <button
                type="submit"
                disabled={submitting || !candidateName || !candidateEmail || !role || !company}
                style={{
                  width: "100%",
                  padding: "12px 0",
                  background:
                    submitting || !candidateName || !candidateEmail || !role || !company
                      ? "#94A3B8"
                      : T.ink,
                  color: T.white,
                  border: "none",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor:
                    submitting || !candidateName || !candidateEmail || !role
                      ? "default"
                      : "pointer",
                  transition: "background 0.15s",
                  letterSpacing: "0.01em",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                {submitting && <Spinner size={14} />}
                {submitting ? "Generating…" : "Generate invite link"}
              </button>

              {/* Cancel */}
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <a
                  href="/coach/recruiter"
                  style={{
                    fontSize: 13,
                    color: T.slate,
                    textDecoration: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </a>
              </div>
            </div>
          </div>

          {/* Responsive grid collapse */}
          <style>{`
            @media (max-width: 720px) {
              .invite-grid {
                grid-template-columns: 1fr !important;
              }
              .invite-submit-desktop { display: none; }
            }
            @media (min-width: 721px) {
              .invite-submit-mobile { display: none; }
            }
          `}</style>
        </form>
      )}
    </RecruiterShell>
  );
}
